import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Bot, 
  User, 
  Clock, 
  Link, 
  X,
  CheckCircle, 
  AlertCircle,
  MessageSquare,
  Sparkles
} from 'lucide-react';

interface Message {
  id: string;
  content: string;
  type: 'user' | 'assistant' | 'system';
  timestamp: Date;
  citations?: Array<{
    title: string;
    type: string;
    url: string;
  }>;
  suggestedActions?: Array<{
    type: string;
    text: string;
  }>;
  confidence?: number;
}

interface SOMAChatInterfaceProps {
  onClose: () => void;
  initialQuery?: string;
  context?: 'project' | 'knowledge' | 'support';
}

export const SOMAChatInterface: React.FC<SOMAChatInterfaceProps> = ({ 
  onClose, 
  initialQuery = '',
  context = 'project'
}) => {
  function getWelcomeMessage(context: string): string {
    switch (context) {
      case 'knowledge':
        return "Hello! I'm SOMA, your AI assistant for Tyler Technologies knowledge base. I can help you find specific articles, explain concepts, or answer questions about any topic in our documentation. What would you like to learn about?";
      case 'support':
        return "Hello! I'm SOMA, your support assistant. I can help you troubleshoot issues, find solutions, or escalate problems to our support team. What can I help you with today?";
      default:
        return "Hello! I'm SOMA, your AI assistant for Tyler Technologies. I can help you with questions about your onboarding project, troubleshooting, best practices, and more. What would you like to know?";
    }
  }

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      content: getWelcomeMessage(context),
      type: 'assistant',
      timestamp: new Date(),
      suggestedActions: [
        { type: 'quick_question', text: 'How do I reset my password?' },
        { type: 'quick_question', text: 'What are the system requirements?' },
        { type: 'quick_question', text: 'How do I configure SSO?' }
      ]
    }
  ]);
  
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      handleSendMessage();
    }
  }, []);
  
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      content: inputValue,
      type: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);
    
    try {
      setTimeout(() => {
        let response = "I understand you're asking about that topic. Let me search our knowledge base for the most relevant information to help you.";
        let citations: Message['citations'] = [];
        let confidence = 0.85;
        let suggestedActions: Message['suggestedActions'] = [];

        if (currentInput.toLowerCase().includes('narrative') || currentInput.toLowerCase().includes('chart')) {
          response = "Based on your project insights, here are narrative and chart suggestions:\n\n**Narrative Recommendations:**\n• Your project shows 85% probability of meeting the September 30th deadline\n• Resource bottleneck detected with Mike Chen at 95% capacity\n• Client engagement has decreased 30% this week - proactive outreach recommended\n\n**Chart Suggestions:**\n• Progress Timeline Chart - Visualize milestones and completion rates\n• Resource Utilization Chart - Show team capacity and bottlenecks\n• Risk Assessment Matrix - Plot impact vs probability of identified risks\n• Engagement Trend Chart - Track client interaction patterns over time\n\nThese visualizations will help communicate project status effectively to stakeholders.";
          citations = [
            { title: "Project Visualization Guide", type: "guide", url: "/knowledge/project-charts" },
            { title: "Stakeholder Communication", type: "article", url: "/knowledge/stakeholder-comms" }
          ];
          confidence = 0.92;
          suggestedActions = [
            { type: 'quick_question', text: 'How do I create these charts in Tyler Technologies?' },
            { type: 'quick_question', text: 'What other visualization options are available?' }
          ];
        } else if (currentInput.toLowerCase().includes('password')) {
          response = "To reset your password, you can use the 'Forgot Password' link on the login page. You'll receive an email with instructions to create a new password. If you don't receive the email within 5 minutes, please check your spam folder or contact support.";
          citations = [
            { title: "Password Reset Guide", type: "guide", url: "/knowledge/password-reset-guide" },
            { title: "Account Security", type: "article", url: "/knowledge/account-security" }
          ];
          confidence = 0.95;
          suggestedActions = [
            { type: 'quick_question', text: 'How do I enable two-factor authentication?' },
            { type: 'quick_question', text: 'What if I still can\'t access my account?' }
          ];
        } else if (currentInput.toLowerCase().includes('sso') || currentInput.toLowerCase().includes('single sign')) {
          response = "To configure Single Sign-On (SSO), you'll need administrator privileges. Navigate to Settings > Authentication > SSO Configuration. You'll need your identity provider's metadata URL or certificate. Our SSO setup guide walks you through the process step-by-step.";
          citations = [
            { title: "SSO Configuration Guide", type: "guide", url: "/knowledge/sso-setup" },
            { title: "Identity Provider Setup", type: "article", url: "/knowledge/identity-providers" }
          ];
          confidence = 0.92;
          suggestedActions = [
            { type: 'quick_question', text: 'Which identity providers are supported?' },
            { type: 'quick_question', text: 'How do I test SSO configuration?' }
          ];
        } else if (currentInput.toLowerCase().includes('system requirements') || currentInput.toLowerCase().includes('requirements')) {
          response = "Tyler Technologies requires: Windows 10/11 or macOS 10.15+, 8GB RAM minimum (16GB recommended), modern web browser (Chrome, Firefox, Safari, Edge), stable internet connection (10 Mbps+), and JavaScript enabled. For mobile access, iOS 13+ or Android 8+ is required.";
          citations = [
            { title: "System Requirements", type: "guide", url: "/knowledge/system-requirements" },
            { title: "Browser Compatibility", type: "article", url: "/knowledge/browser-support" }
          ];
          confidence = 0.98;
          suggestedActions = [
            { type: 'quick_question', text: 'What browsers are officially supported?' },
            { type: 'quick_question', text: 'Are there any network requirements?' }
          ];
        }
        
        const assistantMessage: Message = {
          id: `assistant_${Date.now()}`,
          content: response,
          type: 'assistant',
          timestamp: new Date(),
          citations,
          confidence,
          suggestedActions
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
      
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: "I apologize, but I'm having trouble connecting to our knowledge base right now. Please try again in a moment, or contact support if the issue persists.",
        type: 'system',
        timestamp: new Date()
      }]);
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleQuickQuestion = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };
  
  const renderCitations = (citations: Message['citations']) => {
    if (!citations || citations.length === 0) return null;
    
    return (
      <div className="mt-3 space-y-2">
        <p className="text-xs text-gray-500 font-medium">Related articles:</p>
        {citations.map((citation, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <Link className="w-3 h-3 text-[#4267A1]" />
            <a 
              href={citation.url} 
              className="text-[#4267A1] hover:underline"
              target="_blank" 
              rel="noopener noreferrer"
            >
              {citation.title} ({citation.type})
            </a>
          </div>
        ))}
      </div>
    );
  };
  
  const renderSuggestedActions = (message: Message) => {
    if (!message.suggestedActions || message.suggestedActions.length === 0) return null;
    
    return (
      <div className="mt-3 flex flex-wrap gap-2">
        {message.suggestedActions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleQuickQuestion(action.text)}
            className="text-xs bg-[#4267A1] hover:bg-[#5A7BC4] text-white border border-[#4267A1] px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
          >
            <MessageSquare className="w-3 h-3" />
            {action.text}
          </button>
        ))}
      </div>
    );
  };
  
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <img src="/src/assets/soma-logo.png" alt="SOMA" className="w-14 h-14 object-contain drop-shadow-sm" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <Sparkles className="w-14 h-14 text-[#4267A1] hidden drop-shadow-sm" />
          <div>
            <h3 className="text-lg font-semibold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent drop-shadow-sm">SOMA</h3>
            <p className="text-sm text-gray-600 drop-shadow-sm">AI-powered support for Tyler Technologies</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#8A9150] animate-pulse"></div>
            <span className="text-sm text-gray-600">Online</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                <div className={`flex items-start gap-3 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user' 
                      ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]' 
                      : message.type === 'assistant'
                      ? 'bg-gradient-to-r from-[#8A9150] to-[#A5B366]'
                      : 'bg-orange-500'
                  }`}>
                    {message.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : message.type === 'assistant' ? (
                      <Bot className="w-4 h-4 text-white" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  
                  <div className={`rounded-2xl p-4 ${
                    message.type === 'user'
                      ? 'bg-gradient-to-r from-[#4267A1]/10 to-[#5A7BC4]/10 border border-[#4267A1]/30'
                      : message.type === 'assistant'
                      ? 'bg-gray-50 border border-gray-200'
                      : 'bg-orange-50 border border-orange-200'
                  }`}>
                    <p className="text-gray-900 text-sm leading-relaxed drop-shadow-sm">{message.content}</p>
                    
                    {message.confidence && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3" />
                        <span>Confidence: {Math.round(message.confidence * 100)}%</span>
                      </div>
                    )}
                    
                    {renderCitations(message.citations)}
                    {renderSuggestedActions(message)}
                    
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>{message.timestamp.toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#8A9150] to-[#A5B366] flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#4267A1] rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-[#4267A1] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-[#4267A1] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-6 border-t border-gray-200">
        <div className="flex items-end gap-3">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask SOMA about your Tyler Technologies implementation..."
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4267A1] focus:border-[#4267A1]"
            />
          </div>
          
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] hover:shadow-lg hover:shadow-[#4267A1]/25 text-white p-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-xl"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
